import { Link, useLocation } from "react-router-dom";
import { Banner } from "../components/Banner";
import { Button } from "../components/Button";
import { Badge, PlanBadge, SourceTag } from "../components/Badge";
import { DemoStage } from "../components/DemoStage";
import { EmptyState } from "../components/EmptyState";
import { HighlightAnimation } from "../components/HighlightAnimation";
import { Icon } from "../components/Icon";
import { Skeleton } from "../components/Skeleton";
import { useI18n, type I18nKey } from "../i18n";
import { CATEGORY_ICON, categoryFromPrice, categoryName, useCategories } from "../lib/queries";
import { formatNumber } from "../lib/format";
import { usePlans } from "../lib/billingQueries";
import { CopyrightLine } from "../components/PolicyFooter";

const UNIT_KEY: Record<string, I18nKey> = { sqm: "unit.sqm.short", point: "unit.point", unit: "unit.unit", kg: "unit.kg", cbm: "unit.cbm" };

function Hero({ text, lang }: { text: string; lang: "th" | "en" }) {
  const cut = lang === "en" ? "get a price" : "รู้ราคาประเมิน";
  const i = text.indexOf(cut);
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <em>{cut}</em>
      {text.slice(i + cut.length)}
    </>
  );
}

function Categories() {
  const { t, lang } = useI18n();
  const q = useCategories();
  if (q.isPending) {
    return (
      <div className="cats" aria-busy="true">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} shape="custom" style={{ minHeight: 132, borderRadius: "var(--r-lg)" }} />
        ))}
      </div>
    );
  }
  if (q.isError) {
    return (
      <EmptyState
        tone="danger"
        icon="wifiOff"
        title={t("web.cat.error")}
        body={t("web.load.error.body")}
        action={
          <Button onClick={() => void q.refetch()} iconLeft="refresh">
            {t("common.retry")}
          </Button>
        }
      />
    );
  }
  return (
    <div className="cats">
      {q.data.slice(0, 6).map((c) => (
        <Link key={c.category_id} className="cat" to="/capture" state={{ fresh: true, manualCats: [c.category_id] }}>
          <span className="ci">
            <Icon name={CATEGORY_ICON[c.category_id] ?? "layers"} />
          </span>
          <strong>{categoryName(c, lang)}</strong>
          <span className="small muted num">{t("home.cats.from", { v: formatNumber(categoryFromPrice(c)), u: t(UNIT_KEY[c.unit] ?? "unit.sqm.short") })}</span>
        </Link>
      ))}
    </div>
  );
}

/** S01 หน้าแรก */
export function LandingPage() {
  const { t, lang } = useI18n();
  const plans = usePlans();
  // จำนวนงานฟรีต่อเดือนมาจากแพ็กเกจ Free ที่ Admin ตั้ง ไม่ใช้ข้อความคงที่
  const freeEstimates = plans.data?.find((p) => p.id === "free")?.estimates;
  const deleted = Boolean((useLocation().state as { accountDeleted?: boolean } | null)?.accountDeleted);
  return (
    <>
      {deleted && (
        <div className="landing-deleted">
          <Banner tone="success" title={t("web.del.done.title")}>
            {t("web.del.done.body")}
          </Banner>
        </div>
      )}
      <section className="hero">
        <div className="stack">
          <h1>
            <Hero text={t("home.hero")} lang={lang} />
          </h1>
          <p className="sub">{t("home.sub")}</p>
          <div className="stack-sm" style={{ alignItems: "flex-start" }}>
            <Button variant="primary" size="lg" to="/capture" state={{ fresh: true }} iconLeft="camera" style={{ minHeight: 60, fontSize: "var(--fs-xl)", padding: "0 28px" }}>
              {t("home.cta")}
            </Button>
            <span className="small muted">{freeEstimates !== undefined ? t("web.home.ctaNote", { n: freeEstimates }) : t("web.home.ctaNote.plain")}</span>
          </div>
          <div className="trust">
            <SourceTag source="ai" compact />
            <SourceTag source="ref" />
            <SourceTag source="user" />
            <span style={{ flexBasis: "100%" }}>{t("home.trust")}</span>
          </div>
        </div>
        <DemoStage />
      </section>

      <section className="sec" aria-labelledby="hlT" style={{ paddingTop: 0 }}>
        <h2 className="sr-only" id="hlT">
          {t("home.highlight")}
        </h2>
        <HighlightAnimation />
      </section>

      <section className="sec" aria-labelledby="catT">
        <div className="sec-head">
          <h2 id="catT">{t("home.cats.title")}</h2>
        </div>
        <Categories />
      </section>

      <section className="sec" aria-labelledby="howT">
        <div className="sec-head">
          <h2 id="howT">{t("home.how.title")}</h2>
        </div>
        <ol className="how" style={{ margin: 0, padding: 0 }}>
          <li>{t("home.how.1")}</li>
          <li>{t("home.how.2")}</li>
          <li>{t("home.how.3")}</li>
        </ol>
      </section>

      <section className="sec" aria-labelledby="planT">
        <div className="sec-head">
          <h2 id="planT">{t("home.plans.title")}</h2>
          <Button variant="ghost" to="/pricing" iconRight="right">
            {t("home.plans.all")}
          </Button>
        </div>
        <div className="plans" aria-busy={plans.isPending || undefined}>
          {plans.isPending && [0, 1, 2, 3].map((i) => <Skeleton key={i} shape="custom" style={{ height: 120, borderRadius: 16 }} />)}
          {(plans.data ?? []).map((p) => (
            <div key={p.id} className={p.recommended ? "plan rec" : "plan"}>
              <div className="row-between">
                <PlanBadge plan={p.id} />
                {p.recommended && (
                  <Badge tone="primary" dot={false}>
                    {t("pw.recommended")}
                  </Badge>
                )}
              </div>
              <span className="p">
                {formatNumber(p.price)} ฿<span className="small muted" style={{ fontWeight: 400 }}>{t("pw.perMonth")}</span>
              </span>
              <span className="small">{t("pw.estimates", { n: p.estimates })}</span>
              <span className={p.quote ? "small" : "small subtle"}>{t(p.quote ? "pw.quoteYes" : "pw.quoteNo")}</span>
            </div>
          ))}
          {plans.isError && <p className="small muted">{t("web.load.error.title")}</p>}
        </div>
      </section>

      <footer className="home-footer">
        <p>{t("home.footer")}</p>
        <CopyrightLine />
        <p className="policy-footer-nav">
          <Link className="legal-link" to="/policies">
            {t("web.pol.link")}
          </Link>
          <Link className="legal-link" to="/privacy">
            {t("web.priv.link")}
          </Link>
          <Link className="legal-link" to="/policies#terms">
            {t("web.pol.footer.terms")}
          </Link>
          <Link className="legal-link" to="/policies#refund">
            {t("web.pol.footer.refund")}
          </Link>
          <Link className="legal-link" to="/policies#contact">
            {t("web.pol.footer.contact")}
          </Link>
        </p>
      </footer>
    </>
  );
}

